import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ScopeSelector } from './ScopeSelector';

import { useTranslation } from 'react-i18next';

interface ScopePickerDialogProps {
  templateName: string;
  onConfirm: (scope: 'user' | 'tenant') => void;
  onCancel: () => void;
  isLoading: boolean;
}

export function ScopePickerDialog({
  templateName,
  onConfirm,
  onCancel,
  isLoading,
}: ScopePickerDialogProps) {
  const { t } = useTranslation('pleri/skills');
  const [scope, setScope] = useState<'user' | 'tenant'>('user');

  return (
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onCancel();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl dark:bg-gray-900">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {t('addTemplate', { templateName })}
          </h2>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {t('whoShouldThisSkillBeAvailableTo')}
          </p>
        </div>

        <div className="p-5">
          <ScopeSelector
            value={scope}
            onChange={(s) => {
              if (s === 'user' || s === 'tenant') setScope(s);
            }}
            isAdmin={true}
            excludeTemplate
          />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 dark:border-gray-800">
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="rounded-lg border border-gray-200 px-3.5 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            {t('common:cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(scope)}
            disabled={isLoading}
            className="flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                {t('adding')}
              </>
            ) : (
              t('addSkill')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
